import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { tokenStore } from "@/lib/auth/tokenStore";
import { tenantBlockedStore } from "@/lib/auth/tenantBlockedStore";
import { refreshAccessToken } from "@/lib/api/client";
import { clearAccountScopedState } from "@/lib/storage/accountScopedState";
import { logout as apiLogout } from "@/lib/api/clients/AuthenticationClient";
import type { AuthState } from "@/types/auth";

type AuthContextValue = {
  state: AuthState;

  /**
   * Marks the user as authenticated. The auth-form caller is responsible
   * for completing the IO (login / MFA verify / MFA confirm-setup) and
   * passing the returned `companyId` here. Wraps tokenStore writes +
   * state transition so callers don't have to coordinate them.
   */
  markAuthenticated: (companyId: string, userId?: string) => void;

  /** Marks the user as mid-MFA. Same contract as `markAuthenticated`. */
  markMfaRequired: (type: "totp" | "email", mfaExpiresAt: number) => void;

  /**
   * Single logout entry point: clears tokenStore + tenant-blocked marker
   * + React Query cache + AuthContext state, then best-effort calls the
   * backend to invalidate the session cookies. Local teardown runs first
   * so PrivateRoute redirects before any in-flight query observes a
   * half-state.
   */
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

/** Fired when a session becomes valid again — the twin of `veltric:auth-lost`. */
export const AUTH_RECOVERED_EVENT = "veltric:auth-recovered";

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<AuthState>({ kind: "loading" });

  // Bootstrap on mount: tokens live in HttpOnly cookies — try the
  // refresh endpoint blindly. If it succeeds the browser now holds a
  // fresh access cookie and we treat ourselves as authenticated.
  // companyId is read off localStorage (cached from the last login)
  // so we don't need a separate /me round-trip on cold load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const ok = await refreshAccessToken();
      if (cancelled) return;
      if (!ok) {
        tokenStore.clear();
        setState({ kind: "anonymous" });
        return;
      }
      const cid = tokenStore.getCompanyId();
      if (cid) {
        setState({ kind: "authenticated", companyId: cid });
      } else {
        // Refresh succeeded but we have no cached companyId. Fall
        // through to anonymous — the user must log in once for the
        // cockpit to learn the companyId. Rare in practice (only after
        // a localStorage clear on a still-valid session).
        setState({ kind: "anonymous" });
      }
    })();

    const tearDownLocal = () => {
      tokenStore.clear();
      tenantBlockedStore.clear();
      try { queryClient.clear(); } catch { /* noop */ }
    };
    // DELIBERATELY NOT `clearAccountScopedState()` here. `veltric:auth-lost`
    // means "this session is dead", not "a different person is at the
    // keyboard": the 401 interceptor has no auth-route exemption, so a single
    // mistyped password on the login screen reaches this line, as does any
    // transient refresh failure. Sweeping here destroyed the CURRENT user's
    // unsaved ad-hoc views and every wizard draft in the tab. The sweep is
    // keyed on identity instead — see hydrateAccountPresentation, which also
    // catches the ending this door never sees (a refresh cookie that expired
    // between two visits fires no event at all).
    const onAuthLost = () => {
      tearDownLocal();
      setState({ kind: "anonymous" });
    };
    const onAuthChanged = () => {
      // Defensive safety net: triggered if any non-useAuth code path
      // writes companyId via the cookies shim. Mirrors the bootstrap
      // refresh-success branch.
      const cid = tokenStore.getCompanyId();
      if (cid) setState({ kind: "authenticated", companyId: cid });
      else setState({ kind: "anonymous" });
    };
    window.addEventListener("veltric:auth-lost", onAuthLost);
    window.addEventListener("veltric:auth-changed", onAuthChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("veltric:auth-lost", onAuthLost);
      window.removeEventListener("veltric:auth-changed", onAuthChanged);
    };
  }, [queryClient]);

  const markAuthenticated = useCallback<AuthContextValue["markAuthenticated"]>(
    (companyId, userId) => {
      tokenStore.setCompanyId(companyId);
      if (userId) tokenStore.setUserId(userId);
      setState({ kind: "authenticated", companyId });
      // The counterpart of `veltric:auth-lost`. Anything that paused itself on
      // that event needs to know the session is back — and the most common
      // recovery is the SAME user signing in again, which sweeps nothing and
      // therefore fires no account teardown. Sent as an event rather than
      // read from this context so listeners keep working when they are mounted
      // without an AuthProvider (every provider unit test does that).
      try {
        window.dispatchEvent(new Event(AUTH_RECOVERED_EVENT));
      } catch { /* noop */ }
    },
    [],
  );

  const markMfaRequired = useCallback<AuthContextValue["markMfaRequired"]>(
    (type, mfaExpiresAt) => {
      setState({ kind: "mfa-required", type, mfaExpiresAt });
    },
    [],
  );

  const logout = useCallback(async () => {
    // Local teardown first — instant UI flip. React batches the
    // synchronous tokenStore/queryClient writes with the setState below,
    // so PrivateRoute redirects in one tick.
    tokenStore.clear();
    tenantBlockedStore.clear();
    // Drops every account-bound key (theme, locale, views/filters, background
    // jobs, onboarding flags, sessionStorage) and repaints the default theme,
    // so the login screen and the next account start from one truth.
    clearAccountScopedState();
    try { queryClient.clear(); } catch { /* noop */ }
    // `loggedOut`: PrivateRoute then lands on /auth/login without a `next` —
    // whichever of it and the caller's own navigate("/auth/login") acts last.
    setState({ kind: "anonymous", loggedOut: true });
    // Backend cleanup is fire-and-forget — even if it fails (expired
    // access cookie → 401 → axios refresh fails → veltric:auth-lost on
    // already-anonymous state), local session is already gone.
    void apiLogout().catch(() => { /* noop */ });
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({ state, markAuthenticated, markMfaRequired, logout }),
    [state, markAuthenticated, markMfaRequired, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/**
 * Stateless auth API surface (no React state involved). Pages import these
 * directly when calling auth endpoints that don't transition the auth state
 * machine — password reset OTP, MFA setup flows, account deletion, etc.
 */
export {
  requestPasswordReset,
  resetPasswordByOtp,
  resetPasswordByToken,
  validateResetToken,
  requestEmailUpdate,
  setupMfa,
  confirmMfaSetup,
  disableMfa,
  regenerateRecoveryCodes,
  sendMfaEmailCode,
  isValidOtp,
  resendOtp,
  resendOtpRegisteredUser,
  requestUserRegistration,
  activateAccount,
  resendActivationEmail,
} from "@/lib/api/clients/AuthenticationClient";
